import type { AppSettings, AuditEntry } from '@et/shared';
import { useEffect, useState } from 'react';
import { api, describeError } from '../api/client';
import { dateTime } from '../lib/format';
import { t, useT } from '../lib/i18n';
import { toast } from '../lib/toast';
import { useResource } from '../lib/use-resource';

/** Keys edited on their own pages or guarded by the engine are not editable here. */
const EDITABLE = ['paper_wallet', 'market_data', 'notifications', 'sentiment', 'universe'] as const;

function SettingBox({ name, value, onSaved }: { name: string; value: unknown; onSaved: () => void }): JSX.Element {
  const [text, setText] = useState(JSON.stringify(value, null, 2));
  const tr = useT();
  useEffect(() => { setText(JSON.stringify(value, null, 2)); }, [value]);
  return (
    <div className="card stack">
      <h2 style={{ margin: 0 }}>{tr(name)}</h2>
      <textarea className="mono" rows={Math.min(14, text.split('\n').length + 1)} value={text} onChange={(e) => { setText(e.target.value); }} />
      <div><button className="primary" onClick={() => void (async () => {
        try {
          await api.put('/settings', { [name]: JSON.parse(text) as unknown });
          toast.success(t('{name} saved', { name: t(name) }));
          onSaved();
        } catch (err) {
          toast.error(err instanceof SyntaxError ? t('not valid JSON') : describeError(err));
        }
      })()}>{tr('Save')}</button></div>
    </div>
  );
}

export function Settings(): JSX.Element {
  const settings = useResource<AppSettings>('/settings');
  const [pw, setPw] = useState({ current: '', next: '' });
  const tr = useT();
  return (
    <div className="stack">
      <h1>{tr('Settings')}</h1>
      <div className="grid cols-2">
        {settings.data && EDITABLE.map((k) => <SettingBox key={k} name={k} value={settings.data?.[k]} onSaved={() => void settings.reload()} />)}
      </div>
      <div className="card stack" style={{ maxWidth: 420 }}>
        <h2 style={{ margin: 0 }}>{tr('Change password')}</h2>
        <input type="password" placeholder={tr('current password')} value={pw.current} onChange={(e) => { setPw({ ...pw, current: e.target.value }); }} autoComplete="current-password" />
        <input type="password" placeholder={tr('new password (min 12 characters)')} value={pw.next} onChange={(e) => { setPw({ ...pw, next: e.target.value }); }} autoComplete="new-password" />
        <div><button className="primary" disabled={!pw.current || !pw.next} onClick={() => void api.post('/auth/password', pw).then(() => { toast.success(t('Password changed')); setPw({ current: '', next: '' }); }, (err: unknown) => { toast.error(describeError(err)); })}>{tr('Change')}</button></div>
      </div>
    </div>
  );
}

export function Audit(): JSX.Element {
  const [page, setPage] = useState(1);
  const audit = useResource<{ items: AuditEntry[]; total: number; pageSize: number }>(`/audit?page=${String(page)}&pageSize=50`);
  const pages = audit.data ? Math.max(1, Math.ceil(audit.data.total / audit.data.pageSize)) : 1;
  const tr = useT();
  return (
    <div className="stack">
      <h1>{tr('Audit log')}</h1>
      <div className="card table-wrap">
        <table>
          <thead><tr><th>{tr('Time')}</th><th>{tr('Actor')}</th><th>{tr('Action')}</th><th>{tr('Entity')}</th><th>IP</th><th>{tr('Change')}</th></tr></thead>
          <tbody>{audit.data?.items.map((a) => (
            <tr key={a.id}>
              <td className="muted">{dateTime(a.createdAt)}</td><td>{tr(a.actor)}</td><td>{a.action}</td><td className="muted">{[a.entity, a.entityId].filter(Boolean).join(' ')}</td><td className="muted">{a.ip}</td>
              <td className="mono muted" style={{ whiteSpace: 'normal', fontSize: 11 }}>{a.after === null ? '' : JSON.stringify(a.after).slice(0, 160)}</td>
            </tr>
          ))}</tbody>
        </table>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="small" disabled={page <= 1} onClick={() => { setPage(page - 1); }}>‹</button>
          <span className="muted">{page} / {pages}</span>
          <button className="small" disabled={page >= pages} onClick={() => { setPage(page + 1); }}>›</button>
        </div>
      </div>
    </div>
  );
}
